/**
 * The voice note's two ears on one microphone — `docs/activity.md` §11.2.
 *
 * **The words**: the browser's `SpeechRecognition` with `continuous` and
 * `interimResults`, restarted on `onend` while recording, because every
 * browser stops listening after a silence. Final results are ink, the interim
 * one faint.
 *
 * **The sound**: `MediaRecorder` on the same `getUserMedia` stream —
 * `audio/webm;codecs=opus`, else Safari's `audio/mp4` — with an
 * `AnalyserNode` on it for the 46-bar meter.
 *
 * **Never the recording without the text.** Where the two cannot run at once
 * (a phone that hands the microphone to one of them), the recording is
 * dropped and the note keeps the words; where there is no speech recognition
 * at all, the words are typed into the same box. A recording is only ever
 * saved beside its text.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/** The bars of Board 15 A. */
export const METER_BARS = 46
/** A bar every 1.4 s (thirty at 0:42, as drawn), its height the loudest moment in it. */
const BAR_MS = 1400
const SAMPLE_MS = 140
/** §11.2: amber at 9:30, saved at 10:00. */
export const WARN_AT_SECONDS = 9 * 60 + 30
export const STOP_AT_SECONDS = 10 * 60

export type RecorderPhase = 'starting' | 'recording' | 'paused' | 'stopped'

export interface Recording {
  text: string
  blob: Blob | null
  contentType: string | null
  seconds: number
}

interface SpeechResultList {
  length: number
  [index: number]: { isFinal: boolean; 0: { transcript: string } }
}
interface SpeechLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((event: { resultIndex: number; results: SpeechResultList }) => void) | null
  onend: (() => void) | null
  onerror: ((event: { error?: string }) => void) | null
  start: () => void
  stop: () => void
  abort?: () => void
}

function speechApi(): (new () => SpeechLike) | null {
  if (typeof window === 'undefined') return null
  const host = window as unknown as { SpeechRecognition?: new () => SpeechLike; webkitSpeechRecognition?: new () => SpeechLike }
  return host.SpeechRecognition ?? host.webkitSpeechRecognition ?? null
}

/** What the phone's MediaRecorder will make: opus in webm, else Safari's mp4, else whatever it likes. */
export function recordingType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  for (const type of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported?.(type)) return type
  }
  return ''
}

export interface Recorder {
  phase: RecorderPhase
  seconds: number
  /** The meter, oldest first, newest last; 0–1. */
  levels: number[]
  /** The bar being made now. */
  live: number
  finalText: string
  interim: string
  /** The browser types as somebody talks. False: the box is typed in. */
  hearing: boolean
  /** The sound is being kept as well as the words. */
  keepingSound: boolean
  problem: string | null
  setTyped: (text: string) => void
  pause: () => void
  resume: () => void
  stop: () => Promise<Recording>
}

export function useRecorder(onLimit: () => void): Recorder {
  const [phase, setPhase] = useState<RecorderPhase>('starting')
  const [seconds, setSeconds] = useState(0)
  const [levels, setLevels] = useState<number[]>([])
  const [live, setLive] = useState(0)
  const [finalText, setFinalText] = useState('')
  const [interim, setInterim] = useState('')
  const [hearing] = useState(() => Boolean(speechApi()))
  const [keepingSound, setKeepingSound] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const stream = useRef<MediaStream | null>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const speech = useRef<SpeechLike | null>(null)
  const listening = useRef(false)
  const audio = useRef<AudioContext | null>(null)
  const analyser = useRef<AnalyserNode | null>(null)
  const finals = useRef('')
  const interimRef = useRef('')
  const soundDropped = useRef(false)
  const phaseRef = useRef<RecorderPhase>('starting')
  const elapsed = useRef(0)
  const limit = useRef(onLimit)
  limit.current = onLimit

  const setPhaseBoth = (next: RecorderPhase) => { phaseRef.current = next; setPhase(next) }

  const startSpeech = useCallback(() => {
    const Api = speechApi()
    if (!Api) return
    const run = new Api()
    run.lang = 'en-US'
    run.continuous = true
    run.interimResults = true
    run.onresult = (event) => {
      let pending = ''
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index]!
        const said = result[0].transcript
        if (result.isFinal) {
          finals.current = `${finals.current}${finals.current && !finals.current.endsWith(' ') ? ' ' : ''}${said.trim()}`
          setFinalText(finals.current)
        } else {
          pending += said
        }
      }
      interimRef.current = pending.trim()
      setInterim(interimRef.current)
    }
    run.onerror = (event) => {
      /* The microphone went to the recording: keep the words, drop the sound (§11.2). */
      if (event.error === 'audio-capture' && recorder.current && !soundDropped.current) {
        soundDropped.current = true
        try { recorder.current.stop() } catch { /* already stopped */ }
        recorder.current = null
        chunks.current = []
        setKeepingSound(false)
      }
    }
    /* Every browser stops after a silence: start again while recording. */
    run.onend = () => {
      if (listening.current && phaseRef.current === 'recording') {
        try { run.start() } catch { /* already started */ }
      }
    }
    speech.current = run
    listening.current = true
    try { run.start() } catch { /* already started */ }
  }, [])

  const stopSpeech = useCallback(() => {
    listening.current = false
    try { speech.current?.stop() } catch { /* not started */ }
  }, [])

  /* Start: the microphone, the recorder on it, the meter, the words. */
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const type = recordingType()
      if (navigator.mediaDevices?.getUserMedia && type !== null) {
        try {
          const got = await navigator.mediaDevices.getUserMedia({ audio: true })
          if (cancelled) { got.getTracks().forEach((track) => track.stop()); return }
          stream.current = got
          const made = type ? new MediaRecorder(got, { mimeType: type }) : new MediaRecorder(got)
          made.ondataavailable = (event) => { if (event.data.size > 0) chunks.current.push(event.data) }
          made.start(1000)
          recorder.current = made
          setKeepingSound(true)
          const Context = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
          if (Context) {
            const context = new Context()
            const node = context.createAnalyser()
            node.fftSize = 512
            context.createMediaStreamSource(got).connect(node)
            audio.current = context
            analyser.current = node
          }
        } catch {
          setProblem('The microphone is off for this site, so only the words are kept.')
        }
      }
      if (cancelled) return
      setPhaseBoth('recording')
      startSpeech()
    })()
    return () => {
      cancelled = true
      stopSpeech()
      try { recorder.current?.stop() } catch { /* already stopped */ }
      stream.current?.getTracks().forEach((track) => track.stop())
      void audio.current?.close().catch(() => {})
    }
  }, [startSpeech, stopSpeech])

  /* The clock and the meter. */
  useEffect(() => {
    if (phase !== 'recording') return undefined
    const tick = window.setInterval(() => {
      elapsed.current += 1
      setSeconds(elapsed.current)
      if (elapsed.current >= STOP_AT_SECONDS) limit.current()
    }, 1000)
    /*
     * The meter: a bar every 1.4 s — Board 15 A holds thirty at 0:42 — with
     * the newest one live, so the sound is seen as it is made.
     */
    let loudest = 0
    let since = 0
    const sample = window.setInterval(() => {
      const node = analyser.current
      /* No microphone, no meter: grey bars rather than green ones that heard nothing. */
      if (!node) return
      let level = 0
      if (node) {
        const data = new Uint8Array(node.fftSize)
        node.getByteTimeDomainData(data)
        let sum = 0
        for (const value of data) sum += ((value - 128) / 128) ** 2
        level = Math.min(1, Math.sqrt(sum / data.length) * 4)
      }
      loudest = Math.max(loudest, level)
      since += SAMPLE_MS
      if (since >= BAR_MS) {
        const bar = loudest
        loudest = 0
        since = 0
        setLevels((was) => [...was, bar].slice(-(METER_BARS - 1)))
      }
      setLive(loudest)
    }, SAMPLE_MS)
    return () => { window.clearInterval(tick); window.clearInterval(sample) }
  }, [phase])

  const pause = useCallback(() => {
    if (phaseRef.current !== 'recording') return
    try { recorder.current?.pause() } catch { /* not recording */ }
    stopSpeech()
    setPhaseBoth('paused')
  }, [stopSpeech])

  const resume = useCallback(() => {
    if (phaseRef.current !== 'paused') return
    try { recorder.current?.resume() } catch { /* not paused */ }
    setPhaseBoth('recording')
    startSpeech()
  }, [startSpeech])

  const stop = useCallback(async (): Promise<Recording> => {
    setPhaseBoth('stopped')
    stopSpeech()
    const made = recorder.current
    let blob: Blob | null = null
    if (made && !soundDropped.current) {
      await new Promise<void>((resolve) => {
        made.onstop = () => resolve()
        try { made.stop() } catch { resolve() }
      })
      const type = made.mimeType || recordingType() || 'audio/webm'
      blob = chunks.current.length > 0 ? new Blob(chunks.current, { type }) : null
    }
    stream.current?.getTracks().forEach((track) => track.stop())
    const text = [finals.current, interimRef.current].filter(Boolean).join(' ').trim()
    return { text, blob, contentType: blob?.type ?? null, seconds: elapsed.current }
  }, [stopSpeech])

  const setTyped = useCallback((text: string) => {
    finals.current = text
    setFinalText(text)
  }, [])

  return {
    phase, seconds, levels, live, finalText, interim, hearing, keepingSound, problem, setTyped, pause, resume, stop,
  }
}
